import React from 'react'
import { Autocomplete, Box, Divider, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { AccessViewMode, useViewMode } from '@core/main-state'

import { useSetViewMode, VIEW_MODE_LABEL_KEYS, VIEW_MODES } from './view-mode-api'

const ViewModeSwitcher = () => {
  const { t } = useTranslation('common', { keyPrefix: 'settings' })
  const { viewMode, canSwitchViewMode } = useViewMode()
  const setViewMode = useSetViewMode()

  // Escape hatch #1: gated on the GRANTED server-computed flag, never on
  // `internalAdmin` (which is effective and false in the very modes the user
  // needs this control to get out of).
  if (!canSwitchViewMode) return null

  // The API keeps a 3-value vocabulary while the column stores NULL for
  // "no preview", so a null mode renders as the internal-admin selection.
  const selectedMode: AccessViewMode = viewMode ?? 'internal_admin'

  return (
    <>
      <Divider sx={{ color: 'text.grayishBlue', mt: 2 }}>{t('view_mode')}</Divider>
      <Box
        sx={{ px: 2, py: 1 }}
        data-testid="view-mode-switcher"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Autocomplete
          size="small"
          options={VIEW_MODES}
          value={selectedMode}
          isOptionEqualToValue={(option, value) => option === value}
          getOptionLabel={(option) => t(VIEW_MODE_LABEL_KEYS[option])}
          onChange={(_, newValue) => {
            if (newValue) {
              setViewMode.mutate(newValue)
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('view_mode')} helperText={t('view_mode_help')} />
          )}
          disableClearable
          sx={{ minWidth: 200 }}
        />
      </Box>
    </>
  )
}

export default ViewModeSwitcher
